// Typed condition graph for the Pine visual composer.
//
// Users compose entry/exit signals as trees of indicator comparisons joined by
// AND/OR groups. The graph is what gets persisted and round-tripped through
// the UI; `conditionGraphToExpr` (in conditions-to-ast.ts) converts a graph
// to a PineExpr node for the AST renderer.

export type ConditionSource =
  | { kind: "price"; series: "close" | "open" | "high" | "low" }
  | { kind: "ema"; length: number }
  | { kind: "sma"; length: number }
  | { kind: "rsi"; length: number }
  | { kind: "constant"; value: number };

export type ConditionOperator =
  | "crosses_above"
  | "crosses_below"
  | "greater_than"
  | "less_than"
  | "greater_or_equal"
  | "less_or_equal"
  | "equal"
  | "not_equal";

export type ConditionLeaf = {
  kind: "leaf";
  id: string;
  lhs: ConditionSource;
  op: ConditionOperator;
  rhs: ConditionSource;
};

export type ConditionGroup = {
  kind: "group";
  id: string;
  logic: "and" | "or";
  conditions: ConditionNode[];
};

export type ConditionNode = ConditionLeaf | ConditionGroup;

// `root: null` means "no override - use the template's default signal".
export type ConditionGraph = { root: ConditionNode | null };

export const CONDITION_OPERATOR_LABELS: Record<ConditionOperator, string> = {
  crosses_above: "crosses above",
  crosses_below: "crosses below",
  greater_than: ">",
  less_than: "<",
  greater_or_equal: ">=",
  less_or_equal: "<=",
  equal: "==",
  not_equal: "!=",
};

export const CONDITION_OPERATORS: readonly ConditionOperator[] = [
  "crosses_above",
  "crosses_below",
  "greater_than",
  "less_than",
  "greater_or_equal",
  "less_or_equal",
  "equal",
  "not_equal",
];

export type ConditionSourceKind = ConditionSource["kind"];

export const CONDITION_SOURCE_KIND_LABELS: Record<ConditionSourceKind, string> = {
  price: "Price",
  ema: "EMA",
  sma: "SMA",
  rsi: "RSI",
  constant: "Constant",
};

export function describeSource(source: ConditionSource): string {
  switch (source.kind) {
    case "price":
      return source.series;
    case "ema":
      return `EMA(${source.length})`;
    case "sma":
      return `SMA(${source.length})`;
    case "rsi":
      return `RSI(${source.length})`;
    case "constant":
      return String(source.value);
  }
}

// Empty graph: no override, the template's default signal applies.
export function emptyConditionGraph(): ConditionGraph {
  return { root: null };
}

// Single-leaf seed used when the user toggles override on for the first time.
export function seedLeaf(): ConditionLeaf {
  return {
    kind: "leaf",
    id: cryptoRandomId(),
    lhs: { kind: "ema", length: 9 },
    op: "crosses_above",
    rhs: { kind: "ema", length: 21 },
  };
}

export function seedGroup(logic: "and" | "or" = "and"): ConditionGroup {
  return {
    kind: "group",
    id: cryptoRandomId(),
    logic,
    conditions: [seedLeaf()],
  };
}

function cryptoRandomId(): string {
  // Stable, non-PRNG-quality ID. Used only as a React key + persisted handle
  // for editor operations - never as a security boundary.
  return `c_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Decide what the override toggle should do when flipped. Pulled into a
 * pure helper so the "preserve composed conditions across off/on" rule
 * (audit fix C-2) is unit-testable without rendering React.
 *
 * `current` is what the editor is showing right now.
 * `lastNonNull` is the most recent root the editor saw before any toggle-off.
 *
 * If currently active -> turning off (root = null).
 * If currently inactive -> turning on (restore lastNonNull or seed fresh).
 */
export function nextGraphForOverrideToggle(
  current: ConditionGraph,
  lastNonNull: ConditionNode | null,
): ConditionGraph {
  const active = current.root !== null;
  if (active) return { root: null };
  return { root: lastNonNull ?? seedGroup("and") };
}

// Recursive walk that returns true if every leaf in the graph is structurally
// valid (non-zero indicator lengths, finite constants, non-empty groups).
export function isConditionGraphValid(graph: ConditionGraph): boolean {
  if (!graph.root) return true; // "no override" is valid.
  return isNodeValid(graph.root);
}

function isNodeValid(node: ConditionNode): boolean {
  if (node.kind === "leaf") {
    return isSourceValid(node.lhs) && isSourceValid(node.rhs);
  }
  if (node.conditions.length === 0) return false;
  return node.conditions.every(isNodeValid);
}

function isSourceValid(source: ConditionSource): boolean {
  switch (source.kind) {
    case "price":
      return true;
    case "ema":
    case "sma":
    case "rsi":
      return Number.isInteger(source.length) && source.length >= 1 && source.length <= 500;
    case "constant":
      return Number.isFinite(source.value);
  }
}
